/**
 * web/src/pages/WorkflowList.tsx
 * UI-03: the SPONSORS tab (route /sponsors). Stats banner + filter/search bar
 * + status-colored sponsor-onboarding cards + real-time WS updates + empty
 * state. Since the 2026-07 nav restructure this page is scoped to
 * sponsor_onboarding (+ admin test rows): speaker onboarding lives in the
 * Speakers roster/timeline (/speakers), so speaker_onboarding rows are
 * filtered out below and there is no type dropdown.
 *
 * Source: planning-docs/design_handoff_pixel_dashboard/design_files/pages.jsx
 *         lines 8-117 (WorkflowListPage prototype);
 *         design_handoff_pixel_dashboard/README.md §Pages §Workflows.
 *
 * Data plumbing:
 *  - useStats(eventId) → 4-metric banner (Active = running+paused, Waiting = paused,
 *    Completed total, Failed total). Server stats are not "today"-scoped in V1
 *    (CONTEXT.md "Specifics" — totals are acceptable).
 *  - useWorkflows({eventId, status, type}) → grouped cards
 *  - WS handler (Plan 03 hybrid invalidation) refreshes ['workflows'] + ['stats']
 *    on relevant broadcast events without explicit subscription here.
 *
 * Test workflow visibility (CONTEXT.md "Claude's Discretion"):
 *  - test_workflow rows are hidden from non-admins entirely.
 *  - Admin sees a "Show test workflows" toggle in FilterBar; default off.
 *
 * Threat T-05-07-03: URL ?status= is cast to FilterState['status'] but the
 * server's Zod enum on GET /api/workflows rejects unknown values; toast'd via
 * the queryClient default error handler.
 */
import { useMemo, useState, useEffect } from 'react';
import { differenceInCalendarDays, format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { useEvent } from '../hooks/useEvent';
import { useAuth } from '../hooks/useAuth';
import { useWorkflows } from '../hooks/queries/useWorkflows';
import { useTriggerWorkflow } from '../hooks/mutations/useTriggerWorkflow';
import { useRetryStep } from '../hooks/mutations/useRetryStep';
import { useArchiveWorkflow } from '../hooks/mutations/useArchiveWorkflow';
import { useSetTopbar } from '../components/layout/Topbar';
import { StatsBanner, type StatItem } from '../components/shared/StatsBanner';
import { Empty } from '../components/shared/Empty';
import { Skeleton } from '../components/shared/Skeleton';
import { Button } from '../components/shared/Button';
import { RoleGuard } from '../components/layout/RoleGuard';
import { WorkflowCard } from '../components/workflow/WorkflowCard';
import { FilterBar, type FilterState } from '../components/workflow/FilterBar';
import { deriveWorkflowStatusKey } from '../components/shared/StatusBadge';
import { Icon } from '../lib/icons';
import { workflowTypeLabel } from '../lib/workflow-step-defs';
import type { Workflow } from '../api/types';

/**
 * Does a workflow match a status-filter bucket? Buckets are derived from the
 * current step's status (deriveWorkflowStatusKey), because the workflow's own
 * status is just 'paused' for gate-waiting, rejected, AND awaiting-reply:
 *   active  → running OR paused, but NOT rejected (a rejected gate is stalled,
 *             not actively in-flight — it needs someone to resubmit or skip)
 *   waiting → awaiting approval OR awaiting reply, but NOT rejected
 *   else    → exact match on the derived key (rejected/completed/failed/cancelled)
 */
export function matchesStatusFilter(w: Workflow, f: FilterState['status']): boolean {
  if (f === 'all') return true;
  const key = deriveWorkflowStatusKey(w);
  if (f === 'active') return (w.status === 'running' || w.status === 'paused') && key !== 'rejected';
  if (f === 'waiting') return key === 'paused' || key === 'awaiting_reply';
  return key === f;
}

/** A run of workflows that all started on the same calendar day. */
export interface WorkflowDateGroup {
  /** yyyy-MM-dd bucket key. */
  key: string;
  /** Human subheading: "Today" / "Yesterday" / "Mon, Jun 16" / "Dec 3, 2025". */
  label: string;
  items: Workflow[];
}

/**
 * Human bucket label for a start date relative to `now`. Today/Yesterday for
 * the recent days, weekday + month/day within the current year, and a full
 * date (with year) for older runs.
 */
export function startDateLabel(d: Date, now: Date): string {
  const diff = differenceInCalendarDays(now, d);
  if (diff <= 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (d.getFullYear() === now.getFullYear()) return format(d, 'EEE, MMM d');
  return format(d, 'MMM d, yyyy');
}

/**
 * Sorts workflows newest-first and buckets them by the calendar day they
 * STARTED (createdAt — the engine flips a new workflow to `running` within ms
 * of insert, so createdAt is effectively the start time; mirrors WorkflowCard).
 * Buckets come out newest-day-first; items within a bucket newest-first too.
 *
 * Pure + time-injected (`now`) so it is deterministic under test.
 */
export function groupByStartDate(items: Workflow[], now: Date): WorkflowDateGroup[] {
  const sorted = [...items].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const out: WorkflowDateGroup[] = [];
  const byKey = new Map<string, WorkflowDateGroup>();
  for (const w of sorted) {
    const d = new Date(w.createdAt);
    const key = format(d, 'yyyy-MM-dd');
    let g = byKey.get(key);
    if (!g) {
      g = { key, label: startDateLabel(d, now), items: [] };
      byKey.set(key, g);
      out.push(g);
    }
    g.items.push(w);
  }
  return out;
}

/**
 * Module-level wrapper that injects the wall clock, keeping the component body
 * pure under `react-hooks/purity` (mirrors ActiveReceiptWatch.deriveWatchNow).
 * Labels are static per render — same convention as the cards' relative times.
 */
function buildDateGroups(items: Workflow[]): WorkflowDateGroup[] {
  return groupByStartDate(items, new Date());
}

/**
 * Workflow List page (UI-03). Consumes the active event from useEvent, the
 * authenticated user from useAuth, and the data layer from useWorkflows +
 * useStats. Mirrors filter state to URL search params so back/refresh
 * preserve the user's view.
 *
 * @sideEffects writes filter state to URL via setSearchParams; calls
 *              useSetTopbar to publish the page topbar; mutates via
 *              useTriggerWorkflow / useRetryStep handles.
 * @idempotent yes — pure render of the cached query results.
 */
export function WorkflowList() {
  const { me } = useAuth();
  const { eventId, event } = useEvent();
  const [params, setParams] = useSearchParams();

  const isAdmin = me?.role === 'admin';

  const [filter, setFilter] = useState<FilterState>(() => ({
    search: params.get('q') ?? '',
    status: (params.get('status') as FilterState['status']) ?? 'all',
    showTest: params.get('test') === '1',
    showArchived: params.get('archived') === '1',
  }));

  // Mirror filter state to URL search params (so deep links + back button
  // preserve the user's view). `replace: true` avoids polluting history with
  // a stack of intermediate filter states.
  useEffect(() => {
    const next = new URLSearchParams();
    if (filter.search) next.set('q', filter.search);
    if (filter.status !== 'all') next.set('status', filter.status);
    if (filter.showTest) next.set('test', '1');
    if (filter.showArchived) next.set('archived', '1');
    setParams(next, { replace: true });
    // setParams is stable per RR7 docs; only filter changes drive the effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // Status + type are filtered client-side (below): the status buckets are
  // derived from the current step (a 'paused' workflow can be gate-waiting,
  // rejected, or awaiting a reply — the server status can't tell them apart),
  // and the stat-tile counts must reflect that same split. We fetch the full
  // event list (≤200) once and slice it locally.
  const {
    data: workflows,
    isPending,
    error,
  } = useWorkflows({ eventId: eventId ?? undefined, showArchived: filter.showArchived });
  const trigger = useTriggerWorkflow();
  const retry = useRetryStep();
  const archive = useArchiveWorkflow();

  // Manual sponsor trigger — prompt for the HubSpot deal ID so step 0 has
  // ctx.workflow.context.trigger.dealId. Without this the step throws on
  // `requires ctx.workflow.context.trigger.dealId`. Webhook triggers seed
  // this from the HubSpot payload; the manual path has to ask.
  //
  // For testing convenience we pre-fill the prompt with the deal ID from the
  // most recent sponsor_onboarding workflow on this event (parsed from
  // `triggerSource: 'hubspot_deal:{dealId}'`), so re-running the same deal is
  // a one-tap confirm after clearing prior runs.
  const triggerSponsorWorkflow = (forEventId: string) => {
    const lastDealId = workflows
      ?.filter(
        (w) => w.type === 'sponsor_onboarding' && w.triggerSource.startsWith('hubspot_deal:'),
      )
      .sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0]
      ?.triggerSource.slice('hubspot_deal:'.length);
    const dealId = window
      .prompt('HubSpot Deal ID for this sponsor onboarding:', lastDealId ?? '')
      ?.trim();
    if (!dealId) return; // user cancelled or left blank
    trigger.mutate({
      eventId: forEventId,
      type: 'sponsor_onboarding',
      triggerSource: `hubspot_deal:${dealId}`,
      displayName: `HubSpot deal ${dealId}`,
      initialContext: { trigger: { dealId } },
    });
  };

  useSetTopbar({
    title: 'Sponsors',
    subtitle: event ? `${event.code} · ${event.name}` : undefined,
    actions: (
      <RoleGuard roles={['admin']}>
        <Button
          variant="primary"
          size="sm"
          disabled={!eventId}
          onClick={() => {
            if (!eventId) return;
            triggerSponsorWorkflow(eventId);
          }}
        >
          <Icon name="plus" size={12} />
          Start onboarding
        </Button>
      </RoleGuard>
    ),
  });

  // This tab shows sponsor onboarding only — speaker rows live in the
  // Speakers roster/timeline (/speakers). Test-visibility additionally
  // affects the stat-tile counts (a test row the user can't see shouldn't be
  // counted). Status/search are applied on top of this for the rendered list.
  const visible = useMemo(
    () =>
      (workflows ?? []).filter(
        (w) =>
          w.type !== 'speaker_onboarding' &&
          !(w.type === 'test_workflow' && !(isAdmin && filter.showTest)),
      ),
    [workflows, isAdmin, filter.showTest],
  );

  // Stat-tile counts, derived from the current-step status so 'Waiting' excludes
  // rejected (a rejected gate is paused but not "waiting on someone").
  const counts = useMemo(() => {
    let active = 0;
    let waiting = 0;
    let completed = 0;
    let failed = 0;
    for (const w of visible) {
      // Archived workflows are offline — never counted in the live stat tiles,
      // even when "Show archived" surfaces them in the list below.
      if (w.isArchived) continue;
      const key = deriveWorkflowStatusKey(w);
      if ((w.status === 'running' || w.status === 'paused') && key !== 'rejected') active += 1;
      if (key === 'paused' || key === 'awaiting_reply') waiting += 1;
      else if (key === 'completed') completed += 1;
      else if (key === 'failed') failed += 1;
    }
    return { active, waiting, completed, failed };
  }, [visible]);

  // Derive grouped + filtered list. Status and search are applied client-side
  // (see matchesStatusFilter — the status buckets need the derived
  // current-step status, which the server's status filter can't express).
  const groups = useMemo(() => {
    const lc = filter.search.trim().toLowerCase();
    const filtered = visible.filter((w) => {
      if (!matchesStatusFilter(w, filter.status)) return false;
      if (lc && !w.displayName.toLowerCase().includes(lc)) return false;
      return true;
    });
    const byType = new Map<string, Workflow[]>();
    for (const w of filtered) {
      const arr = byType.get(w.type) ?? [];
      arr.push(w);
      byType.set(w.type, arr);
    }
    return Array.from(byType.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([type, items]) => ({ type, label: workflowTypeLabel(type), items }));
  }, [visible, filter.search, filter.status]);

  // 4 stat tiles. Failed is colored red via valueClassName (handoff §Stats).
  const statItems: StatItem[] = [
    {
      label: 'Active',
      value: counts.active,
      delta: 'running + waiting',
      onClick: () => setFilter((f) => ({ ...f, status: 'active' })),
    },
    {
      label: 'Waiting',
      value: counts.waiting,
      onClick: () => setFilter((f) => ({ ...f, status: 'waiting' })),
    },
    {
      label: 'Completed',
      value: counts.completed,
      onClick: () => setFilter((f) => ({ ...f, status: 'completed' })),
    },
    {
      label: 'Failed',
      value: counts.failed,
      valueClassName: 'text-failed',
      onClick: () => setFilter((f) => ({ ...f, status: 'failed' })),
    },
  ];

  return (
    <div className="max-w-[1200px]">
      <StatsBanner stats={statItems} className="mb-6" />
      <FilterBar value={filter} onChange={setFilter} isAdmin={!!isAdmin} />

      {isPending && (
        <div className="flex flex-col gap-2.5">
          <Skeleton variant="card" />
          <Skeleton variant="card" />
          <Skeleton variant="card" />
        </div>
      )}

      {error && (
        <Empty
          icon={<Icon name="alert" size={20} />}
          title="Couldn't load workflows"
          description={error instanceof Error ? error.message : 'Unknown error'}
        />
      )}

      {!isPending && !error && workflows && visible.length === 0 && (
        <Empty
          icon={<Icon name="sponsor" size={20} />}
          title="No sponsor onboardings yet for this event"
          description="Onboarding starts automatically when a HubSpot deal closes, or start one manually."
          action={
            <RoleGuard roles={['admin']}>
              <Button
                variant="primary"
                size="sm"
                className="mt-4"
                onClick={() => {
                  if (!eventId) return;
                  triggerSponsorWorkflow(eventId);
                }}
              >
                <Icon name="plus" size={12} />
                Start onboarding
              </Button>
            </RoleGuard>
          }
        />
      )}

      {!isPending && !error && workflows && visible.length > 0 && groups.length === 0 && (
        <Empty
          icon={<Icon name="filter" size={20} />}
          title="No onboardings match your filters"
          description="Adjust the filters above or clear them."
        />
      )}

      {groups.map((g) => {
        const typeKey: 'sponsor' | 'speaker' | 'test' =
          g.type === 'sponsor_onboarding'
            ? 'sponsor'
            : g.type === 'speaker_onboarding'
              ? 'speaker'
              : 'test';
        const tintBg =
          typeKey === 'sponsor'
            ? 'bg-type-sponsor-soft text-type-sponsor'
            : typeKey === 'speaker'
              ? 'bg-type-speaker-soft text-type-speaker'
              : 'bg-bg-muted text-text-muted';
        const groupIcon =
          typeKey === 'sponsor' ? 'building' : typeKey === 'speaker' ? 'user' : 'workflow';
        return (
          <section key={g.type} className="mb-7">
            <div className="flex items-center gap-3 mb-3.5">
              <span
                className={`w-[22px] h-[22px] rounded-md grid place-items-center ${tintBg}`}
                aria-hidden="true"
              >
                <Icon name={groupIcon} size={12} />
              </span>
              <h2 className="text-[11px] font-semibold uppercase tracking-wider">{g.label}</h2>
              <span className="text-[11px] text-text-subtle tabular">({g.items.length})</span>
              <span className="flex-1 h-px bg-border" />
            </div>
            {/* Date-started subgroups: cluster a type's workflows by the day
                they kicked off so a long list reads chronologically. */}
            {buildDateGroups(g.items).map((dg) => (
              <div key={dg.key} className="mb-4 last:mb-0">
                <div className="flex items-center gap-2 mb-2 pl-0.5">
                  <h3 className="text-[10.5px] font-medium uppercase tracking-wider text-text-subtle">
                    {dg.label}
                  </h3>
                  <span className="text-[10.5px] text-text-faint tabular">{dg.items.length}</span>
                  <span className="flex-1 h-px bg-border/50" />
                </div>
                {dg.items.map((w) => (
                  <WorkflowCard
                    key={w.id}
                    workflow={w}
                    onRetry={(stepIdx) => retry.mutate({ workflowId: w.id, stepIndex: stepIdx })}
                    lastFailedStepIndex={w.status === 'failed' ? w.currentStepIndex : undefined}
                    lastErrorMessage={
                      w.status === 'failed' && w.failureReason ? w.failureReason : undefined
                    }
                    onArchiveToggle={(id, archived) => archive.mutate({ id, archived })}
                  />
                ))}
              </div>
            ))}
          </section>
        );
      })}
    </div>
  );
}
